import { createFileRoute, Link } from '@tanstack/react-router'
import { HelpLayout } from '@/components/help-center/help-layout'
import { HelpArticleContent } from '@/components/help-center/help-article'

const howToSchema = {
  '@context': 'https://schema.org',
  '@type': 'HowTo',
  name: 'Import contacts from iPhone',
  description: 'Enable the Contact Picker and import iPhone contacts into MessageDrafts.',
  step: [
    { '@type': 'HowToStep', text: 'Open Settings and enable Safari Contact Picker API.' },
    { '@type': 'HowToStep', text: 'Open MessageDrafts in Safari and tap Import from Phone.' },
    { '@type': 'HowToStep', text: 'Select contacts and confirm the import.' },
    { '@type': 'HowToStep', text: 'Review contacts and continue to compose.' },
  ],
}

export const Route = createFileRoute('/_public/app/help/import-iphone')({
  component: ImportIphonePage,
  head: () => ({
    meta: [
      { title: 'Import Contacts from iPhone | MessageDrafts Help' },
      {
        name: 'description',
        content:
          'Step-by-step guide to importing contacts from iPhone using the Contact Picker in Safari.',
      },
    ],
    scripts: [
      {
        type: 'application/ld+json',
        children: JSON.stringify(howToSchema),
      },
    ],
  }),
})

function ImportIphonePage() {
  return (
    <HelpLayout
      title="Import contacts from iPhone"
      description="Use Safari’s Contact Picker to select people quickly and start drafting messages."
    >
      <HelpArticleContent
        slug="import-iphone"
        title="Overview"
        description="This guide is for iPhone users who want to import contacts directly from their device."
        tags={['iphone', 'import', 'contacts', 'safari']}
        devices={['iphone']}
      >
        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">Prerequisites</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>iPhone running iOS 17 or later.</li>
            <li>Safari browser (required for the Contact Picker).</li>
            <li>HTTPS connection to MessageDrafts.</li>
          </ul>
        </section>

        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">Step-by-step</h3>
          <ol className="list-decimal pl-5 space-y-2">
            <li>
              Open <strong>Settings → Safari → Advanced → Feature Flags</strong>.
            </li>
            <li>Enable the <strong>Contact Picker API</strong> flag.</li>
            <li>Reload MessageDrafts in Safari and open the Import step.</li>
            <li>
              Tap <strong>Import from Phone</strong>, then choose the contacts you want.
            </li>
            <li>Confirm the selection to continue.</li>
          </ol>
        </section>

        <figure className="space-y-2">
          <img
            src="/help/iphone-contact-picker.svg"
            alt="iPhone Safari Contact Picker settings for enabling the Contact Picker API"
            className="w-full rounded-xl border border-slate-200"
          />
          <figcaption className="text-xs text-slate-500">
            Enable the Contact Picker API flag in Safari settings.
          </figcaption>
        </figure>

        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">Troubleshooting</h3>
          <ul className="list-disc pl-5 space-y-1">
            <li>
              <strong>No Import from Phone button:</strong> Ensure Safari is updated and the
              flag is enabled.
            </li>
            <li>
              <strong>Contact picker does not appear:</strong> Reload the page after enabling
              the flag.
            </li>
            <li>
              <strong>Only some contacts show:</strong> Verify contact permissions under
              Settings → Safari → Contacts.
            </li>
          </ul>
        </section>

        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">Notes</h3>
          <p>
            If you prefer not to enable the Contact Picker, you can manually enter contacts or upload a CSV instead. See
            the <Link to="/app/help/import-android" className="underline">
              CSV import notes
            </Link>{' '}
            for formatting tips.
          </p>
        </section>

        <section className="space-y-3">
          <h3 className="text-xl font-semibold text-slate-900">Privacy reminder</h3>
          <p>
            MessageDrafts never sends messages automatically. You stay in control inside your
            phone’s Messages app. Review our{' '}
            <Link to="/app/help/privacy-security" className="underline">
              privacy and security
            </Link>{' '}
            details for more.
          </p>
        </section>
      </HelpArticleContent>
    </HelpLayout>
  )
}
